import { useEffect, useState, memo } from "react";
import CV from "../../assets/Sunil_Kublalsingh.pdf";
import { FaGithub, FaLinkedin } from "react-icons/fa";
import { useInView } from "react-intersection-observer";
import CodeWindow from "./CodeWindow";
import BackgroundGradients from "./BackgroundGradients";
import ActiveStatusBadge from "./ActiveStatusBadge";

const TypingThing = memo(({ toRotate, active }) => {
  const [loopNum, setLoopNum] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [text, setText] = useState("\u00A0");
  const [delta, setDelta] = useState(200);
  const period = 500;

  useEffect(() => {
    if (!active) return; // pause updates when not active

    let ticker = setInterval(() => {
      tick();
    }, delta);

    return () => {
      clearInterval(ticker);
    };
  }, [text, active, delta]);

  const tick = () => {
    let i = loopNum % toRotate.length;
    let fullText = toRotate[i];
    let newText = isDeleting
      ? fullText.substring(0, text.length - 1) // if deleting, then remove one character
      : fullText.substring(0, text.length + 1); // if not deleting, then add one character

    setText(newText);

    // If deleting, we want to remove characters faster than adding them
    if (isDeleting) {
      // if statement to prevent from deleting too quickly (which would be almost instant and not look good)
      if (delta > 100) {
        setDelta((prevDelta) => prevDelta / 2);
      }
    }
    // If we are not deleting and newText is the same as fullText, then we want to start deleting (we also reset the speed to the slowest)
    if (!isDeleting && newText === fullText) {
      setIsDeleting(true);
      setDelta(period);
    }
    // If we are deleting and newText is empty, then we want to start typing the next word (we also reset the speed to the slowest)
    else if (isDeleting && newText === "") {
      setIsDeleting(false);
      setDelta(100);
      setLoopNum(loopNum + 1);
      setText("\u00A0");
    }
  };
  return <span className="typingText"> {text}</span>;
});

const Intro = () => {
  const { ref: introRef, inView: introIsVisible } = useInView();
  // Scroll to projects section
  const handleScrollDown = () => {
    const projectsSection = document.getElementById("projects");
    if (projectsSection) {
      projectsSection.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const toRotate = [
    "Full Stack Developer",
    "Coffee Addict",
    "Problem Solver",
    "Tech Enthusiast",
    "Comp Eng Student",
    "Innovator",
  ];

  return (
    <section id="home" className="px-4 sm:px-12 lg:px-24 relative min-h-screen flex items-center justify-center overflow-hidden bg-mesh">
      <BackgroundGradients />

      <div className="max-w-7xl mx-auto px-6 pt-20 relative z-10 w-full">
        <div className="grid lg:grid-cols-12 gap-16 items-center">

          {/* Left Column: Content */}
          <div className="lg:col-span-7 flex flex-col gap-8" ref={introRef}>
            {/* Active Status Badge */}
            {/* <ActiveStatusBadge /> */}

            <div className="space-y-6">
              <h1 className="text-6xl md:text-8xl font-display font-extrabold leading-[1.1] mb-6">
                Hi! I'm <span className="gradient-text">Sunil</span>
              </h1>

              <div className="text-2xl md:text-4xl font-medium text-slate-500 dark:text-slate-400 font-display flex items-center gap-4 min-h-[50px]">
                <span className="text-slate-900 dark:text-white font-mono opacity-80">&gt;</span>
                <TypingThing toRotate={toRotate} active={introIsVisible} />
              </div>
            </div>

            <p className="text-lg md:text-xl text-slate-600 dark:text-slate-400 leading-relaxed max-w-xl font-light">
              Driven by curiosity and a desire to innovate, with the goal of
              contributing to cutting-edge technologies that shape the future.
            </p>

            <div className="flex flex-wrap items-center gap-8 mt-4">
              <a
                href={CV}
                target="_blank"
                rel="noreferrer"
                className="bg-primary hover:bg-primary-hover text-white px-10 py-4 rounded-2xl text-[16px] font-bold transition-all no-underline shadow-[0_10px_40px_rgb(var(--color-primary-rgb)/0.4)] hover:-translate-y-1 active:translate-y-0 text-center"
              >
                Download Resume
              </a>

              <div className="flex items-center gap-6">
                <a
                  href="https://github.com/hulksunil"
                  target="_blank"
                  rel="noreferrer"
                  className="p-3.5 glass hover:bg-primary dark:hover:bg-primary/20 border border-black/5 dark:border-white/10 rounded-xl transition-all text-slate-900 dark:text-white hover:text-white hover:scale-110 active:scale-95 shadow-lg group"
                  aria-label="GitHub"
                >
                  <FaGithub size={26} />
                </a>
                <a
                  href="https://www.linkedin.com/in/Sunil-Kublalsingh/"
                  target="_blank"
                  rel="noreferrer"
                  className="p-3.5 glass hover:bg-primary dark:hover:bg-primary/20 border border-black/5 dark:border-white/10 rounded-xl transition-all text-slate-900 dark:text-white hover:text-white hover:scale-110 active:scale-95 shadow-lg group"
                  aria-label="LinkedIn"
                >
                  <FaLinkedin size={26} />
                </a>
              </div>
            </div>
          </div>

          {/* Right Column: Code Window */}
          <div className="lg:col-span-5 hidden lg:block perspective-1000">
            <div className="animate-float">
              <div className="transform transition-transform duration-700 shadow-none dark:shadow-2xl">
                <CodeWindow />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Down Indicator - Pushed down to avoid overlap */}
      <button
        type="button"
        onClick={handleScrollDown}
        aria-label="Scroll down to projects"
        className="hidden md:flex lg:flex absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex-col items-center gap-2 opacity-50 hover:opacity-100 transition-opacity bg-transparent border-0 p-0"
      >
        <span className="text-[9px] text-slate-500 uppercase tracking-[0.3em] font-bold">Scroll Down</span>
        <div className="w-5 h-8 rounded-full border-2 border-slate-300 dark:border-slate-700 flex justify-center p-1">
          <div className="w-1 h-1.5 bg-primary rounded-full animate-bounce"></div>
        </div>
      </button>

      {/* Transiition for the gradient to look smooth at the bottom */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-24 bg-gradient-to-b from-transparent to-background dark:to-background-dark" />
    </section>
  );
};

export default memo(Intro);
